import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
	CustomButton,
	CustomIconButton,
	CustomTooltip,
	icon,
	IconComponent,
	LimitedAutocompleteTextField,
	SidePanel,
	SidePanelBox,
	SidePanelContent,
	ViewTabs,
} from "../../../../common/components";
import CommentBox from "../../../../common/components/comment-box/CommentBox";
import { translate } from "../../../../common/providers";
import { setDisableKeyEvents } from "../../../../common/slice";
import { isNonEmptyArray } from "../../../../common/utils";
import { CommitmentService } from "../../../../api";
import styles from "./CommitmentComments.module.css";
import { useApi } from "../../../../common/hooks";
import { setCommentIndicator } from "../../slice/commitmentTrackingSlice";

const CommitmentComments = ({ open, onClose, commitmentId }) => {
	const dispatch = useDispatch();
	const user = useSelector(({ context }) => context.user);
	const teamMembers = useSelector(({ context }) => context.teamMembers);
	const disableKeyEvents = useSelector(({ context }) => context.disableKeyEvents);
	const [counters, setCounters] = useState({ opened: 0, closed: 0 });
	const [comments, setComments] = useState({ contents: [] });
	const [selectedTab, setSelectedTab] = useState("open");
	const [tempComment, setTempComment] = useState("");
	const [isActiveNewComment, setIsActiveNewComment] = useState(false);
	const [shiftPressed, setShiftPressed] = useState(false);
	const [toRefresh, setToRefresh] = useState(true);
	const [toRefreshCounters, setToRefreshCounters] = useState(true);
	const { call: getCommentsCount } = useApi(CommitmentService.getCommentsCount);
	const { call: getAllComments } = useApi(CommitmentService.getAllComments);
	const { call: addComment } = useApi(CommitmentService.addComment);
	const tabHeaders = useMemo(
		() => [
			{
				id: "open",
				label: translate("common:comment.conversation.open", { count: counters?.opened ?? 0 }),
			},
			{
				id: "close",
				label: translate("common:comment.conversation.closed", { count: counters?.closed ?? 0 }),
			},
		],
		[counters]
	);
	useEffect(() => {
		if (disableKeyEvents) {
			dispatch(setDisableKeyEvents(false));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [disableKeyEvents]);
	useEffect(() => {
		if (commitmentId && toRefreshCounters) {
			getCommentsCount({ commitmentId })
				.then((response) => {
					const data = response?.data || response;
					setCounters(data);
					setToRefreshCounters(false);
				})
				.catch(console.error);
		}
	}, [commitmentId, getCommentsCount, toRefreshCounters]);
	useEffect(() => {
		if (open && commitmentId) {
			setToRefresh(true);
			setToRefreshCounters(true);
		}
	}, [open, commitmentId]);
	useEffect(() => {
		if (commitmentId && toRefresh && open) {
			const isOpen = selectedTab === "open";
			getAllComments({ commitmentId }, { page: 0, limit: 25, isOpen })
				.then((response) => {
					const data = response?.data || response;
					setComments({
						...data,
						contents: (data.contents || []).map((com) => ({ ...com, openMore: true })),
					});
					setToRefresh(false);
				})
				.catch(console.error);
		}
	}, [commitmentId, getAllComments, selectedTab, toRefresh, open]);

	const handleDisplayPrevious = useCallback(() => {
		const isOpen = selectedTab === "open";
		getAllComments({ commitmentId }, { page: (comments.pageNumber || 0) + 1, limit: 25, isOpen })
			.then((response) => {
				const data = response?.data || response;
				const { contents, ...rest } = data;
				setComments((prev) => ({
					...rest,
					contents: [
						...(prev.contents || []),
						...(contents || []).map((com) => ({ ...com, openMore: true })),
					],
				}));
			})
			.catch(console.error);
	}, [commitmentId, getAllComments, selectedTab, comments.pageNumber]);

	const handleAddComment = useCallback(() => {
		if (!tempComment?.trim()) {
			return;
		}
		addComment({ commitmentId }, { value: tempComment })
			.then((response) => {
				const data = response?.data || response;
				const newComment = { ...data, childrenCount: 0, hasChild: false, isOpenForDiscussion: true };
				if (isNonEmptyArray(comments?.contents)) {
					setComments((prev) => ({ ...prev, contents: [newComment, ...prev.contents] }));
				} else {
					setToRefresh(true);
				}
				setToRefreshCounters(true);
				setTempComment("");
				dispatch(setDisableKeyEvents(false));
				setIsActiveNewComment(false);
				dispatch(setCommentIndicator({ commitmentId, indicator: true }));
			})
			.catch(console.error);
	}, [commitmentId, addComment, tempComment, comments?.contents, dispatch]);

	const handleBlur = useCallback(() => {
		dispatch(setDisableKeyEvents(false));
		setIsActiveNewComment(false);
	}, [dispatch]);

	const handleFocus = () => {
		dispatch(setDisableKeyEvents(true));
		setIsActiveNewComment(true);
	};
	const handleChangeTempComment = (newComment) => {
		if ((/\S/.test(newComment) && (tempComment?.length === 0 || !tempComment)) || tempComment?.length > 0) {
			setTempComment(newComment);
		}
	};
	const handleChangeTab = (_, tab) => {
		setSelectedTab(tab);
		setComments({ contents: [] });
		setToRefresh(true);
	};
	useEffect(() => {
		const handleKeyReleased = (e) => {
			if (isActiveNewComment && e.key === "Shift") {
				setShiftPressed(false);
			}
		};
		const handleKeyDetect = (e) => {
			if (isActiveNewComment && e.key === "Shift") {
				setShiftPressed(true);
			}
			if (isActiveNewComment && e.key === "Enter" && !shiftPressed) {
				const autocompleteOpen = document.querySelector(
					"[role='listbox'][class*='_mentions__suggestions__list_']"
				);
				if (!autocompleteOpen && tempComment?.length > 0 && /\S/.test(tempComment)) {
					handleAddComment();
				}
			}
			if (isActiveNewComment && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
				e.preventDefault();
				const textField = e.target;
				if (e.key === "ArrowLeft") {
					if (textField.selectionStart > 0) {
						textField.setSelectionRange(textField.selectionStart - 1, textField.selectionStart - 1);
					}
				} else if (e.key === "ArrowRight") {
					if (textField.selectionStart < textField.value.length) {
						textField.setSelectionRange(textField.selectionStart + 1, textField.selectionStart + 1);
					}
				}
			}
			if (isActiveNewComment && e.key === "Escape") {
				handleBlur();
				setTempComment("");
			}
		};
		window.addEventListener("keydown", handleKeyDetect, true);
		window.addEventListener("keyup", handleKeyReleased, true);
		return () => {
			window.removeEventListener("keydown", handleKeyDetect, true);
			window.removeEventListener("keyup", handleKeyReleased, true);
		};
	}, [isActiveNewComment, shiftPressed, tempComment, handleAddComment, handleBlur]);

	const handleUpdateComment = useCallback(({ parentId, childId, data }) => {
		if (parentId) {
			setComments((prev) => ({
				...prev,
				contents: prev.contents.map(
					(c) =>
						(c.commentId === parentId && {
							...c,
							comments: {
								...c.comments,
								contents: c.comments.contents.map(
									(child) => (child.commentId === childId && data) || child
								),
							},
						}) ||
						c
				),
			}));
		} else if (data?.commentId) {
			setComments((prev) => ({
				...prev,
				contents: prev.contents.map((com) => (com.commentId === data.commentId && { ...com, ...data }) || com),
			}));
		}
	}, []);
	const handleAttachChildren = useCallback((id, data) => {
		setComments((prev) => ({
			...prev,
			contents: prev.contents.map(
				(com) =>
					(com.commentId === id && {
						...com,
						comments: { ...(com.comments || {}), ...data },
						openMore: true,
					}) ||
					com
			),
		}));
	}, []);
	const handleOpenConversation = useCallback((id) => {
		setComments((prev) => ({
			...prev,
			contents: prev.contents.map((com) => (com.commentId === id && { ...com, openMore: true }) || com),
		}));
	}, []);
	const handleDeleteComment = useCallback(
		(id, parentId, isLast) => {
			if (!parentId) {
				setComments((prev) => ({
					...prev,
					contents: prev.contents.filter((c) => c.commentId !== id),
				}));
				if (counters.opened + counters.closed === 1) {
					dispatch(setCommentIndicator({ commitmentId, indicator: false }));
				}
			} else {
				setComments((prev) => ({
					...prev,
					contents: prev.contents.map(
						(com) =>
							(com.commentId === parentId && {
								...com,
								openMore: !!isLast,
								comments: {
									...com.comments,
									contents: com.comments.contents.filter((child) => child.commentId !== id),
								},
							}) ||
							com
					),
				}));
			}
			setToRefreshCounters(true);
		},
		[commitmentId, counters, dispatch]
	);
	const handleRefresh = useCallback(() => {
		setToRefresh(true);
	}, []);
	const handleRefreshCounters = useCallback(() => {
		setToRefreshCounters(true);
	}, []);
	const HistoryRowRender = useCallback(
		({ comment }) => (
			<CommentBox
				key={comment.commentId}
				isParent
				comment={comment}
				commitmentId={commitmentId}
				disableEditing={false}
				isOpenConversation={selectedTab === "open"}
				teamMembers={teamMembers}
				onAttachChildren={handleAttachChildren}
				onDeleteComment={handleDeleteComment}
				onOpenConversation={handleOpenConversation}
				onRefresh={handleRefresh}
				onRefreshCounters={handleRefreshCounters}
				onUpdateComment={handleUpdateComment}
			/>
		),
		[
			commitmentId,
			handleRefresh,
			handleRefreshCounters,
			selectedTab,
			teamMembers,
			handleUpdateComment,
			handleAttachChildren,
			handleOpenConversation,
			handleDeleteComment,
		]
	);

	return (
		<SidePanel open={open} position="right" size={40} onClose={onClose}>
			<SidePanelContent title={translate("commitment-tracking.details.tab.comments")} onClose={onClose}>
				<div className={styles.container}>
					<ViewTabs
						className={styles.tabs}
						selectedTab={selectedTab}
						tabIdentifier="id"
						tabs={tabHeaders}
						variant="scrollable"
						onChange={handleChangeTab}
					/>
					{selectedTab === "open" && (
						<SidePanelBox className={styles.container__newComment} hasShadow={false}>
							<div className={styles.container__line}>
								<CustomTooltip showWhenOverflow title={user.displayName}>
									<div className={styles.name}>{user.displayName}</div>
								</CustomTooltip>
							</div>
							<LimitedAutocompleteTextField
								multiline
								className={styles.textField}
								data={teamMembers}
								displayLength={tempComment?.length > 0}
								helperTextClassName={styles.helperTextClassName}
								maxLength={1024}
								placeholder={translate("common:comment.add-comment")}
								rows={tempComment?.length > 3 ? 4 : 1}
								size="sm"
								trigger={null}
								value={tempComment}
								onBlur={handleBlur}
								onChange={handleChangeTempComment}
								onFocus={handleFocus}
							/>
							{tempComment?.length > 0 && (
								<div className={styles.line__rightItems}>
									<CustomIconButton
										btnClassName={styles.btnClassName}
										data-testid="comment.send.btn"
										icon={icon.faPaperPlane}
										iconClassName={styles.iconClassName}
										tooltip={translate("common:comment.send-button")}
										variant="outlined"
										onClick={handleAddComment}
									/>
								</div>
							)}
						</SidePanelBox>
					)}
					<div className={styles.listScrollContainer}>
						{(comments?.contents?.length > 0 &&
							comments?.contents?.map((prime) => (
								<div key={prime.commentId}>
									<HistoryRowRender comment={prime} />
								</div>
							))) || (
							<div className={styles.emptyStateContainer}>
								<IconComponent color="var(--blue-500)" icon={icon.faInfoCircle} />
								{translate("common:comment.empty-state.no-results")}
							</div>
						)}
						{comments?.hasMore && (
							<CustomButton
								className={styles.displayMore__button}
								color="secondary"
								variant="outlined"
								onClick={handleDisplayPrevious}
							>
								{translate("common:comment.row.display-previous")}
							</CustomButton>
						)}
					</div>
				</div>
			</SidePanelContent>
		</SidePanel>
	);
};

CommitmentComments.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	commitmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

CommitmentComments.defaultProps = {
	commitmentId: null,
};

export default CommitmentComments;
